import * as React from 'react';
import PropTypes from 'prop-types';
import UserDetailsForm from '../../ts-components/page-registration/user-details';
import appState from '../../state/AppStateContainer';
import Tabs from '../../components/shared/Tabs'

import './styles.css';
import { ServiceUser } from '../../services/service-user';
import AppChangePassword from './../app-password/app-change-password';
import _ from 'lodash';
import AppLayout from '../../components/layout/app-layout/app-layout';
import { useTranslation } from 'react-i18next';
import { CustomToggle } from '../../components/shared';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      className="custom-tab-style"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {(value === index) && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function Settings() {
  const [value, setValue] = React.useState(0);
  const [userData, setUserData] = React.useState(null);
  const [t] = useTranslation('profile');
  const notificationPreferences = appState.getUserNotificationPreferences();

  React.useEffect(() => {
    const fetchAndSetUser = async () => {
      const userId = appState.getUserId();
      let user = await ServiceUser.getUserById(userId);
      setUserData(user);
    };
    fetchAndSetUser();
  }, []);

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  // Preferences are user-global, so product access is checked across every org
  // the user belongs to rather than just the current one. The appState helpers
  // normalise legacy orgs that have no activeProducts at all to PURCHASING.
  const organizations = appState.getOrganizations();
  const hasSalesOrganization = organizations.some(org => appState.isSalesActive(org));
  const hasPurchasingOrganization = organizations.some(org => appState.isPurchasingActive(org));
  // The save endpoint (PUT updateUserPreferences) only admits customer roles —
  // don't show supplier/guest users toggles they can neither save nor receive.
  const isCustomerUser = appState.isCustomerUser();

  // The first three emails only go to supplier-side (SALES) users and the last
  // three to buyer-side (PURCHASING) users, so each toggle stays hidden unless
  // the user belongs to an org with the matching product.
  // defaultChecked mirrors each backend's gate: weeklyDigest sends only on a
  // strict `true` (opt-in), the rest send unless `false` (opt-out).
  const notificationRows = [
    {
      key: 'weeklyDigest',
      visible: hasSalesOrganization,
      defaultChecked: false,
      title: t('notifications-settings.weekly-digest.title', 'Weekly digest for published quotations'),
      description: t('notifications-settings.weekly-digest.description', 'Every wednesday we send a summary of all the new published quotations from this week.'),
    },
    {
      key: 'supplierInsuranceReminder',
      visible: hasSalesOrganization,
      defaultChecked: true,
      title: t('notifications-settings.supplier-insurance-reminder.title', 'Insurance expiry reminders'),
      description: t('notifications-settings.supplier-insurance-reminder.description', 'Get an email when your liability insurance is about to expire so you can renew it in time.'),
    },
    {
      key: 'supplierProfileReminder',
      visible: hasSalesOrganization,
      defaultChecked: true,
      title: t('notifications-settings.supplier-profile-reminder.title', 'Profile completion reminders'),
      description: t('notifications-settings.supplier-profile-reminder.description', 'Get an email reminder when your supplier profile is missing information that helps buyers find you.'),
    },
    {
      key: 'bidReceived',
      visible: hasPurchasingOrganization,
      defaultChecked: true,
      title: t('notifications-settings.bid-received.title', 'New bids'),
      description: t('notifications-settings.bid-received.description', 'Get an email as soon as a supplier submits a bid in your projects.'),
    },
    {
      key: 'intentReceived',
      visible: hasPurchasingOrganization,
      defaultChecked: true,
      title: t('notifications-settings.intent-received.title', 'Bid intents'),
      description: t('notifications-settings.intent-received.description', 'Get an email when a supplier declares whether they intend to bid in your projects.'),
    },
    {
      key: 'messageDigest',
      visible: hasPurchasingOrganization,
      defaultChecked: true,
      title: t('notifications-settings.message-digest.title', 'Daily message summary'),
      description: t('notifications-settings.message-digest.description', 'Every morning we send a summary of yesterday\'s unread messages from suppliers.'),
    },
  ].filter(row => isCustomerUser && row.visible);

  const showNotificationsTab = notificationRows.length > 0;
  const tabs = [
    t('tabs.profile', 'Profil'),
    ...(showNotificationsTab ? [t('tabs.notifications', 'Aviseringar')] : []),
    t('tabs.changePassword', 'Byt lösenord'),
  ];
  const changePasswordIndex = showNotificationsTab ? 2 : 1;

  return (
    <AppLayout title={t('pageTitle', 'Din profil')}>
      <div className="settings-container">
        <Tabs tabs={tabs} selected={value} onChange={handleChange} />
        <TabPanel value={value} index={0}>
          <div className="profile-wrapper">
            <UserDetailsForm
              isProfile
              user={userData}
              setParentUser={setUserData}
            />
          </div>
        </TabPanel>
        {showNotificationsTab && (
          <TabPanel value={value} index={1}>
            <div className="notification-settings-wrapper">
              {notificationRows.map(row => {
                const checked = notificationPreferences[row.key] ?? row.defaultChecked;
                return (
                  <div key={row.key}>
                    <div>
                      <p>{row.title}</p>
                      <span>{row.description}</span>
                    </div>
                    <CustomToggle checked={checked} onChange={() => appState.updateUserNotificationPreferences(row.key, !checked)} />
                  </div>
                );
              })}
            </div>
          </TabPanel>
        )}
        <TabPanel value={value} index={changePasswordIndex}>
          <div className="change-password-wrapper">
            <AppChangePassword
              email={_.get(userData, 'email', 'Loading email...')}
              orgSlug={appState._getCurrentOrgUrl()}
              changePassword
            />
          </div>
        </TabPanel>
      </div>
    </AppLayout>
  );
}
